import { PencilIcon } from "@heroicons/react/20/solid";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { Button } from "@/atoms-react/button/Button.react";
import { Dropdown } from "@/atoms-react/dropdown/index.react";
import Flag from "@/atoms-react/flag/Flag.react";
import InputLabel from "@/atoms-react/input/Input.react";
import InputDate from "@/atoms-react/input/InputDate.react";
import Loader from "@/atoms-react/loader/Loader.react";
import { Base, Label } from "@/atoms-react/text/Text.react";
import { useCompany } from "@/features/company/hooks/use-company";
import { companyDetails, DeclarativeCompany } from "@/features/company/types";
import { useUser } from "@/features/user/hooks/use-user";
import { FAVOURITE_COUNTRY_CODES } from "@/features/utils/constants";
import { usei18n } from "@/features/utils/hooks/use-i18n";
import { Themes } from "@/types/theme";
import { extractErrorDescription } from "@/utils/internal-code-handler";
import { formStyle } from "../form-style";
import { getDirtyValues } from "../utils";
import { buildDeclarativeDataFormLegalEntity } from "./utils";

type CompanyDeclarativePropsType = {
  companyDetails?: companyDetails;
  isCompanyDataFetching: boolean;
  isAdmin: boolean;
  actionEnabled?: boolean;
};

export const DeclarativeCompanyForm = ({
  companyDetails,
  isCompanyDataFetching,
  isAdmin = false,
  actionEnabled
}: CompanyDeclarativePropsType) => {
  const translation = useTranslation(["pass", "error"]);
  const { t } = translation;
  const { getLanguage } = usei18n();

  const [date, setDate] = useState<Date | null>(
    companyDetails?.legalEntity.DateOfIncorporation?.date
      ? new Date(companyDetails?.legalEntity.DateOfIncorporation?.date)
      : null
  );

  // Get Countries //
  const { useCountries, useActivitiesNace } = useUser();
  const { data: countries } = useCountries(getLanguage());

  // Get Activities NACE //
  const { data: activitiesNace, isPending: loagindActivitiesNace } = useActivitiesNace(
    getLanguage()
  );

  // Get Legal forms //
  const { useLegalForm } = useUser();
  const { data: legalForms, isPending: loadingLf } = useLegalForm();

  // mutation to edit declaratives informations //
  const { partialEditCompanyMutation } = useCompany();
  const { mutateAsync: partialEditCompanyData, isPending: isLoadingDeclarativeCompanyEdit } =
    partialEditCompanyMutation();
  // Variables //
  const legalEntity = companyDetails?.legalEntity;
  const disabled =
    !isAdmin &&
    (!!companyDetails?.legalEntity?.UserValidation ||
      !!companyDetails?.legalEntity.AgentReviewFinish ||
      actionEnabled === false);

  // useForm - START //
  const {
    handleSubmit,
    control,
    formState: { errors, isDirty, dirtyFields },
    watch,
    reset
  } = useForm<DeclarativeCompany>({
    defaultValues: buildDeclarativeDataFormLegalEntity(legalEntity)
  });

  useEffect(() => {
    reset(buildDeclarativeDataFormLegalEntity(companyDetails?.legalEntity));
  }, [companyDetails?.legalEntity]);

  const onSubmit: SubmitHandler<DeclarativeCompany> = async (data: DeclarativeCompany) => {
    if (!data.legalEntityUuid) {
      toast.error(t("olkypass.general.company_error"));
      return;
    }

    try {
      await partialEditCompanyData({
        ...getDirtyValues(dirtyFields, data, false),
        legalEntityUuid: data.legalEntityUuid
      });
      toast.success(t("olkypass.accountPage.notifications.success.profile.update"));
    } catch (e: unknown) {
      toast.error(extractErrorDescription(e, translation));
    }
  };
  // useForm - END //

  const getTitle = (title: string, showSeparator: boolean = true) => {
    return (
      <div className={showSeparator ? "px-6" : ""}>
        {showSeparator && <hr className={"border-slate-50 dark:border-slate-700"} />}
        <Base className="my-8 text-slate-300 text-xl">{title}</Base>
      </div>
    );
  };

  return (
    <form>
      <div className="w-full md:mb-6 md:p-0 2xl:mb-0">
        <div className={formStyle.container}>
          <div className={twMerge(formStyle.containerLine, "justify-between")}>
            {getTitle(t("olkypass.accountPage.basicInfo.tabs.legal_entity_information"), false)}

            <Button
              className="w-max shrink-0"
              loading={isLoadingDeclarativeCompanyEdit || isCompanyDataFetching}
              disabled={disabled || !isDirty}
              onClick={async (e) => {
                e.preventDefault();
                await handleSubmit(onSubmit)();
              }}
              type="button"
              icon={<PencilIcon />}
              theme={Themes.primary}
            >
              {t("olkypass.general.save")}
            </Button>
          </div>

          <div className={formStyle.containerLine}>
            <div className={formStyle.containerLineTitle}>
              <Label>{t("business.input.company_rcs_label")}</Label>
            </div>
            <div className={formStyle.containerLineContent}>
              <Controller
                control={control}
                render={({ field: { onChange, value } }) => (
                  <InputLabel
                    required={false}
                    disabled={disabled}
                    data-tooltip={t("business.input.company_rcs_label")}
                    placeholder={t("business.input.company_rcs_placeholder")}
                    error={errors.rcsNumber ? t("atoms:general.required") : ""}
                    value={value ?? undefined}
                    onChange={onChange}
                  />
                )}
                name="rcsNumber"
              />
            </div>
          </div>

          <div className={formStyle.containerLine}>
            <div className={formStyle.containerLineTitle}>
              <Label>* {t("business.input.company_name_label")}</Label>
            </div>
            <div className={formStyle.containerLineContent}>
              <Controller
                control={control}
                rules={{
                  required: true
                }}
                render={({ field: { onChange, value } }) => (
                  <InputLabel
                    required={false}
                    disabled={disabled}
                    noLatinCheck={true}
                    data-tooltip={t("business.input.company_name_placeholder")}
                    placeholder={""}
                    value={value ?? undefined}
                    error={errors.legalName ? t("atoms:general.required") : ""}
                    onChange={onChange}
                  />
                )}
                name="legalName"
              />
            </div>
          </div>

          <div className={formStyle.containerLine}>
            <div className={formStyle.containerLineTitle}>
              <Label>* {t("business.input.country_date_registration")}</Label>
            </div>
            <div className={formStyle.containerLineContent}>
              <Controller
                control={control}
                rules={{
                  required: true
                }}
                render={({ field: { onChange, value } }) => (
                  <Dropdown
                    placeholder={t("flow.id_control.input.country_of_residence_placeholder")}
                    disabled={disabled}
                    search={true}
                    data-tooltip={t("olkypass.companyPage.basicInfo.input.country")}
                    items={
                      (countries?.countries ?? []).map((country, index) => ({
                        key: country.uuid ?? index.toString(),
                        value: country.defaultName,
                        icon: <Flag countryCode={country.alpha2} />,
                        render: ({ key }) => (
                          <div className={"flex w-full pr-5"} key={key}>
                            <Flag countryCode={country.alpha2} className="!w-6 !h-6 rounded-sm" />
                            <Base className={"ml-2"}>{country.defaultName}</Base>
                          </div>
                        ),
                        groupKey: FAVOURITE_COUNTRY_CODES.includes(country.alpha2.toLowerCase())
                          ? "default"
                          : undefined
                      })) ?? []
                    }
                    value={value}
                    autoComplete="country"
                    className={errors.countryIncorporationUuid && "border-red-500"}
                    onChange={(value) => {
                      onChange(value.key);
                    }}
                  />
                )}
                name="countryIncorporationUuid"
              />
            </div>
            <div className="h-4 w-4" />
            <div className={formStyle.containerLineContent}>
              <Controller
                control={control}
                rules={{
                  required: true
                }}
                render={({ field: { onChange } }) => (
                  <InputDate
                    autoComplete="bday"
                    disabled={disabled}
                    value={date}
                    className={errors.incorporationDate && "border-red-500"}
                    placeholder={t("flow.id_control.input.birth_date_placeholder")}
                    onChange={(value) => {
                      const dateTime = value?.toISOString().replace("Z", "").replace("T", "");
                      setDate(value);
                      onChange(dateTime);
                    }}
                  />
                )}
                name="incorporationDate"
              />
            </div>
          </div>

          <div className={formStyle.containerLine}>
            <div className={formStyle.containerLineTitle}>
              <Label>* {t("olkypass.companyPage.basicInfo.input.legalForm")}</Label>
            </div>
            <div className={formStyle.containerLineContent}>
              {loadingLf ? (
                <Loader />
              ) : (
                <Controller
                  control={control}
                  rules={{ required: false }}
                  render={({ field: { onChange, value } }) => (
                    <Dropdown
                      placeholder={t("olkypass.companyPage.basicInfo.input.legalForm")}
                      disabled={disabled}
                      items={(legalForms ?? []).map((legalForm) => ({
                        key: legalForm.uuid,
                        value: legalForm.name
                      }))}
                      value={value}
                      search={true}
                      autoComplete="country"
                      className={twMerge("w-full", errors.legalFormUuid && "border-red-500")}
                      data-tooltip={t("olkypass.companyPage.basicInfo.input.legalForm")}
                      onChange={({ key }) => {
                        onChange(key);
                      }}
                    />
                  )}
                  name="legalFormUuid"
                />
              )}
            </div>
          </div>

          <div className={formStyle.containerLine}>
            <div className={formStyle.containerLineTitle}>
              <Label>
                {`* `} {t("olkypass.companyPage.basicInfo.input.activity_sector_nace")}
              </Label>
            </div>
            <div className={formStyle.containerLineContent}>
              {loagindActivitiesNace ? (
                <Loader />
              ) : (
                <div className="flex w-full flex-col">
                  <Controller
                    control={control}
                    rules={{
                      required: true
                    }}
                    render={({ field: { onChange, value } }) => (
                      <Dropdown
                        value={value?.toString()}
                        disabled={disabled}
                        placeholder={t("olkypass.companyPage.basicInfo.input.activity_sector_nace")}
                        data-tooltip={t(
                          "olkypass.companyPage.basicInfo.input.activity_sector_nace"
                        )}
                        items={(activitiesNace?.activities_nace ?? []).map((activityNace) => ({
                          key: activityNace.uuid,
                          value: activityNace.defaultName
                        }))}
                        search={true}
                        className={twMerge("w-full", errors.activityNaceUuid && "border-red-500")}
                        onChange={({ key }) => onChange(key)}
                      />
                    )}
                    name="activityNaceUuid"
                  />
                </div>
              )}
            </div>
          </div>

          {getTitle(t("olkypass.companyPage.basicInfo.input.address"))}

          <div className={formStyle.containerLine}>
            <div className={formStyle.containerLineTitle}>
              <Label>{t("olkypass.companyPage.basicInfo.input.road")}</Label>
            </div>
            <div className={formStyle.containerLineContent}>
              <Controller
                control={control}
                rules={{
                  required: true
                }}
                render={({ field: { onChange, value } }) => (
                  <InputLabel
                    required={false}
                    disabled={disabled}
                    data-tooltip={t("olkypass.companyPage.basicInfo.input.road")}
                    placeholder={t("flow.id_control.input.address_placeholder")}
                    error={errors.address?.address ? t("atoms:general.required") : ""}
                    value={value ?? undefined}
                    onChange={onChange}
                  />
                )}
                name="address.address"
              />
            </div>
          </div>

          <div className={formStyle.containerLine}>
            <div className={formStyle.containerLineTitle}>
              <Label>{t("olkypass.companyPage.basicInfo.input.city_zip")}</Label>
            </div>
            <div className={formStyle.containerLineContent}>
              <Controller
                control={control}
                rules={{
                  required: true
                }}
                render={({ field: { onChange, value } }) => (
                  <InputLabel
                    required={false}
                    disabled={disabled}
                    data-tooltip={t("flow.id_control.input.business.city_label")}
                    placeholder={t("flow.id_control.input.business.city_placeholder")}
                    error={errors.address?.city ? t("atoms:general.required") : ""}
                    value={value ?? undefined}
                    onChange={onChange}
                  />
                )}
                name="address.city"
              />
            </div>
            <div className="h-4 w-4"></div>
            <div className={formStyle.containerLineContent}>
              <Controller
                control={control}
                rules={{
                  required: true
                }}
                render={({ field: { onChange, value } }) => (
                  <InputLabel
                    required={false}
                    data-tooltip={t("flow.id_control.input.zip_label")}
                    placeholder={t("flow.id_control.input.zip_placeholder")}
                    error={errors.address?.zipCode ? t("atoms:general.required") : ""}
                    value={value ?? undefined}
                    disabled={disabled}
                    onChange={onChange}
                  />
                )}
                name="address.zipCode"
              />
            </div>
          </div>

          <div className={formStyle.containerLine}>
            <div className={formStyle.containerLineTitle}>
              <Label>{t("olkypass.companyPage.basicInfo.input.country")}</Label>
            </div>
            <div className={formStyle.containerLineContent}>
              <Controller
                control={control}
                rules={{
                  required: true
                }}
                render={({ field: { onChange, value } }) => (
                  <Dropdown
                    placeholder={t("flow.id_control.input.country_of_residence_placeholder")}
                    data-tooltip={t("olkypass.companyPage.basicInfo.input.country")}
                    disabled={disabled}
                    items={
                      (countries?.countries ?? []).map((country, index) => ({
                        key: country.uuid ?? index.toString(),
                        value: country.defaultName,
                        icon: <Flag countryCode={country.alpha2} />,
                        render: ({ key }) => (
                          <div className={"flex w-full"} key={key}>
                            <Flag countryCode={country.alpha2} className="!w-6 !h-6 rounded-sm" />
                            <Base className={"ml-2"}>{country.defaultName}</Base>
                          </div>
                        ),
                        groupKey: FAVOURITE_COUNTRY_CODES.includes(country.alpha2.toLowerCase())
                          ? "default"
                          : undefined
                      })) ?? []
                    }
                    value={value}
                    autoComplete="country"
                    className="w-full"
                    search={true}
                    onChange={(value) => {
                      onChange(value.key);
                    }}
                  />
                )}
                name="address.countryUuid"
              />
            </div>
          </div>

          {getTitle(t("olkypass.companyPage.basicInfo.input.principal_address"))}

          <div className={formStyle.containerLine}>
            <div className={formStyle.containerLineTitle}>
              <Label>{t("olkypass.companyPage.basicInfo.input.road_activity")}</Label>
            </div>
            <div className={formStyle.containerLineContent}>
              <Controller
                control={control}
                rules={{
                  required:
                    watch("activityAddress.city") ||
                    watch("activityAddress.zipCode") ||
                    watch("activityAddress.countryUuid")
                }}
                render={({ field: { onChange, value } }) => (
                  <InputLabel
                    disabled={disabled}
                    data-tooltip={t("olkypass.companyPage.basicInfo.input.road")}
                    placeholder={t("flow.id_control.input.address_placeholder")}
                    value={value ?? undefined}
                    onChange={(value) => onChange(value)}
                    error={errors.activityAddress?.address ? t("atoms:general.required") : ""}
                  />
                )}
                name="activityAddress.address"
              />
            </div>
          </div>
          <div className={formStyle.containerLine}>
            <div className={formStyle.containerLineTitle}>
              <Label>{t("olkypass.companyPage.basicInfo.input.city_zip_activity")}</Label>
            </div>
            <div className={formStyle.containerLineContent}>
              <Controller
                control={control}
                rules={{
                  required:
                    watch("activityAddress.address") ||
                    watch("activityAddress.zipCode") ||
                    watch("activityAddress.countryUuid")
                }}
                render={({ field: { onChange, value } }) => (
                  <InputLabel
                    required={false}
                    disabled={disabled}
                    data-tooltip={t("flow.id_control.input.business.city_label")}
                    placeholder={t("flow.id_control.input.business.city_placeholder")}
                    value={value ?? undefined}
                    onChange={(value) => onChange(value)}
                    error={errors.activityAddress?.city ? t("atoms:general.required") : ""}
                  />
                )}
                name="activityAddress.city"
              />
            </div>
            <div className="h-4 w-4"></div>
            <div className={formStyle.containerLineContent}>
              <Controller
                control={control}
                rules={{
                  required:
                    watch("activityAddress.city") ||
                    watch("activityAddress.address") ||
                    watch("activityAddress.countryUuid")
                }}
                render={({ field: { onChange, value } }) => (
                  <InputLabel
                    required={false}
                    disabled={disabled}
                    data-tooltip={t("flow.id_control.input.zip_label")}
                    placeholder={t("flow.id_control.input.zip_placeholder")}
                    value={value ?? undefined}
                    onChange={(value) => {
                      onChange(value);
                    }}
                    error={errors.activityAddress?.zipCode ? t("atoms:general.required") : ""}
                  />
                )}
                name="activityAddress.zipCode"
              />
            </div>
          </div>

          <div className={formStyle.containerLine}>
            <div className={formStyle.containerLineTitle}>
              <Label>{t("olkypass.companyPage.basicInfo.input.country_activity")}</Label>
            </div>
            <div className={formStyle.containerLineContent}>
              <Controller
                control={control}
                rules={{
                  required:
                    watch("activityAddress.city") ||
                    watch("activityAddress.zipCode") ||
                    watch("activityAddress.address")
                }}
                render={({ field: { onChange, value } }) => (
                  <Dropdown
                    placeholder={t("flow.id_control.input.country_of_residence_placeholder")}
                    data-tooltip={t("olkypass.companyPage.basicInfo.input.country_activity")}
                    disabled={disabled}
                    items={
                      (countries?.countries ?? []).map((country, index) => ({
                        key: country.uuid ?? index.toString(),
                        value: country.defaultName,
                        icon: <Flag countryCode={country.alpha2} />,
                        render: ({ key }) => (
                          <div className={"flex w-full"} key={key}>
                            <Flag countryCode={country.alpha2} className="!w-6 !h-6 rounded-sm" />
                            <Base className={"ml-2"}>{country.defaultName}</Base>
                          </div>
                        ),
                        groupKey: FAVOURITE_COUNTRY_CODES.includes(country.alpha2.toLowerCase())
                          ? "default"
                          : undefined
                      })) ?? []
                    }
                    value={value}
                    search={true}
                    autoComplete="country"
                    className={twMerge(
                      "w-full",
                      errors.activityAddress?.countryUuid && "border-red-500"
                    )}
                    onChange={(value) => onChange(value.key)}
                  />
                )}
                name="activityAddress.countryUuid"
              />
            </div>
          </div>

          {getTitle(t("olkypass.companyPage.basicInfo.input.other_info"))}

          <div className={formStyle.containerLine}>
            <div className={formStyle.containerLineTitle}>
              <Label>{t("olkypass.companyPage.basicInfo.input.website_url")}</Label>
            </div>
            <div className={formStyle.containerLineContent}>
              <Controller
                control={control}
                render={({ field: { onChange, value } }) => (
                  <InputLabel
                    required={false}
                    disabled={disabled}
                    data-tooltip={t("olkypass.companyPage.basicInfo.input.website_url")}
                    value={value ?? undefined}
                    onChange={(value) => onChange(value)}
                  />
                )}
                name="website"
              />
            </div>
          </div>

          <div className={formStyle.containerLine}>
            <div className={formStyle.containerLineTitle}>
              <Label>{t("olkypass.companyPage.basicInfo.input.brand")}</Label>
            </div>
            <div className={formStyle.containerLineContent}>
              <Controller
                control={control}
                render={({ field: { onChange, value } }) => (
                  <InputLabel
                    required={false}
                    disabled={disabled}
                    data-tooltip={t("olkypass.companyPage.basicInfo.input.brand")}
                    value={value ?? undefined}
                    onChange={(value) => onChange(value)}
                  />
                )}
                name="brand"
              />
            </div>
          </div>

          <div className={formStyle.containerLine}>
            <div className={formStyle.containerLineTitle}>
              <Label>{t("olkypass.companyPage.basicInfo.input.vatNumber")}</Label>
            </div>
            <div className={formStyle.containerLineContent}>
              <Controller
                control={control}
                render={({ field: { onChange, value } }) => (
                  <InputLabel
                    required={false}
                    disabled={disabled}
                    data-tooltip={t("olkypass.companyPage.basicInfo.input.vatNumber")}
                    value={value ?? undefined}
                    onChange={(value) => onChange(value)}
                  />
                )}
                name="vatNumber"
              />
            </div>
          </div>

          <div className={formStyle.containerLine}>
            <div className={formStyle.containerLineTitle}>
              <Label>{t("flow.id_control.input.country_nif_label")}</Label>
            </div>
            <div className={formStyle.containerLineContent}>
              <Controller
                control={control}
                name="taxCountryUuid"
                render={({ field: { onChange, value } }) => (
                  <Dropdown
                    placeholder={t("flow.id_control.input.country_nif_label")}
                    data-tooltip={t("flow.id_control.input.country_nif_label")}
                    required={false}
                    disabled={disabled}
                    search={true}
                    items={
                      (countries?.countries ?? []).map((country, index) => ({
                        key: country.uuid ?? index.toString(),
                        value: country.defaultName,
                        icon: <Flag countryCode={country.alpha2} />,
                        render: ({ key }) => (
                          <div className={"flex w-full"} key={key}>
                            <Flag countryCode={country.alpha2} className="!w-6 !h-6 rounded-sm" />
                            <Base className={"ml-2"}>{country.defaultName}</Base>
                          </div>
                        ),
                        groupKey: FAVOURITE_COUNTRY_CODES.includes(country.alpha2.toLowerCase())
                          ? "default"
                          : undefined
                      })) ?? []
                    }
                    value={value}
                    autoComplete="country"
                    className={twMerge("w-full", errors?.taxCountryUuid && "border-red-500")}
                    onChange={(value) => onChange(value.key)}
                  />
                )}
              />
            </div>
          </div>

          <div className={formStyle.containerLine}>
            <div className={formStyle.containerLineTitle}>
              <Label>{t("olkypass.companyPage.basicInfo.input.nif")}</Label>
            </div>
            <div className={formStyle.containerLineContent}>
              <Controller
                control={control}
                disabled={watch("taxCountryUuid") === null}
                name="taxNumber"
                render={({ field: { onChange, value } }) => (
                  <InputLabel
                    required={false}
                    disabled={disabled}
                    data-tooltip={t("olkypass.companyPage.basicInfo.input.nif")}
                    placeholder={t("olkypass.companyPage.basicInfo.input.nif")}
                    value={value ?? undefined}
                    onChange={(value) => {
                      onChange(value);
                    }}
                  />
                )}
              />
            </div>
          </div>

          <div className={formStyle.containerLine}>
            <div className={formStyle.containerLineTitle}>
              <Label>N° LEI </Label>
            </div>
            <div className={formStyle.containerLineContent}>
              <Controller
                control={control}
                render={({ field: { onChange, value } }) => (
                  <InputLabel
                    required={false}
                    value={value ?? undefined}
                    disabled={disabled}
                    data-tooltip={"N° LEI"}
                    onChange={(value) => {
                      onChange(value);
                    }}
                  />
                )}
                name="leiNumber"
              />
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};
